/**
 * =============================================
 * 전체 지점 포스팅 관리 페이지
 * - 카드 형태로 전체 지점의 포스팅 정보 표시
 * - /api/social/posts/all API 사용
 * =============================================
 */

import { useState, useEffect } from 'react';
import { 
  Card, 
  CardContent, 
  Typography, 
  Box, 
  Button, 
  Grid, 
  TextField, 
  MenuItem,
  Avatar,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Pagination,
  Paper,
  Divider
} from '@mui/material';
import { 
  Close, 
  CheckCircle, 
  Schedule, 
  Error as ErrorIcon,
  Facebook,
  Instagram 
} from '@mui/icons-material';
import { axiosInstance } from '../../api/axiosInstance';
import { useData } from '../../hooks';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import Footer from 'examples/Footer';

/**
 * 전체 지점 포스팅 관리 페이지
 * 모든 지점의 소셜 포스팅 현황을 카드 형태로 표시
 */
function SocialPostsAll() {
  const { user } = useData();
  const [posts, setPosts] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedBranch, setSelectedBranch] = useState('all');
  const [selectedPlatform, setSelectedPlatform] = useState('all');
  const [filteredPosts, setFilteredPosts] = useState([]);
  const [selectedPost, setSelectedPost] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [page, setPage] = useState(1);
  const postsPerPage = 12;

  // 지점 목록 조회
  const fetchBranches = async () => {
    console.log('🏢 지점 목록 조회 시작');

    if (!user?.accessToken) {
      console.log('❌ 인증 토큰이 없습니다.');
      return;
    }

    try {
      console.log('🔑 토큰 정보:', user.accessToken.substring(0, 20) + '...');
      const response = await axiosInstance.get('/api/admin/branch/branchlist?page=1&limit=1000');

      console.log('📊 지점 목록 응답:', response.data);
      const branchData = response.data?.data?.users || response.data?.data || [];
      setBranches(branchData);
      console.log('✅ 지점 목록 조회 완료:', branchData.length, '개 지점');
    } catch (error) {
      console.error('❌ 지점 목록 조회 실패:', error);
      console.error('상태 코드:', error.response?.status);
      console.error('응답 데이터:', error.response?.data);
    }
  };

  // 데이터 매핑 및 지점 정보 추가
  const mapPostsWithBranchInfo = (postsData) => {
    if (!Array.isArray(postsData)) {
      console.log('⚠️ 포스팅 데이터가 배열이 아닙니다:', typeof postsData);
      return [];
    }

    return postsData.map(post => {
      // branchId로 지점 정보 찾기
      const branch = branches.find(b => b.id === post.branchId);

      return {
        id: post.id,
        branchId: post.branchId,
        branchName: branch ? branch.branch_name || branch.name : `지점 ID: ${post.branchId}`,
        reviewId: post.reviewId,
        platform: post.platform,
        postId: post.postId,
        content: post.content,
        images: post.images || [],
        hashtags: post.hashtags || [],
        likes: post.likes || 0,
        comments: post.comments || 0,
        shares: post.shares || 0,
        postedAt: post.postedAt,
        updatedAt: post.updatedAt,
        postUrl: post.postUrl,
      };
    }).sort((a, b) => new Date(b.postedAt) - new Date(a.postedAt));
  };

  // 소셜 포스팅 조회
  const fetchPosts = async () => {
    console.log('📱 소셜 포스팅 조회 시작');

    if (!user?.accessToken) {
      console.log('❌ 인증 토큰이 없습니다.');
      return;
    }

    try {
      console.log('🔑 토큰 정보:', user.accessToken.substring(0, 20) + '...');
      const response = await axiosInstance.get('/api/social/posts/all');

      console.log('📊 포스팅 응답:', response.data);
      const postsData = response.data?.data || response.data || [];

      const mappedPosts = mapPostsWithBranchInfo(postsData);
      setPosts(mappedPosts);
      console.log('✅ 포스팅 조회 완료:', mappedPosts.length, '개 포스팅');
    } catch (error) {
      console.error('❌ 포스팅 조회 실패:', error);
      console.error('상태 코드:', error.response?.status);
      console.error('응답 데이터:', error.response?.data);
    } finally {
      setLoading(false);
    }
  };

  // 필터링 적용
  const applyFilters = () => {
    let filtered = posts;

    // 지점 필터
    if (selectedBranch !== 'all') {
      filtered = filtered.filter(post => post.branchId === parseInt(selectedBranch));
    }

    // 플랫폼 필터 (Threads 추가)
    if (selectedPlatform !== 'all') {
      filtered = filtered.filter(post => post.platform === selectedPlatform);
    }

    // 검색어 필터
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(post => 
        post.content?.toLowerCase().includes(term) ||
        post.branchName?.toLowerCase().includes(term) ||
        post.hashtags?.some(tag => tag.toLowerCase().includes(term))
      );
    }

    setFilteredPosts(filtered);
    setPage(1);
  };

  // 상세보기 핸들러
  const handleViewDetail = (post) => {
    setSelectedPost(post);
    setDetailOpen(true);
  };

  // 날짜 포맷팅 함수
  const formatDate = (dateString) => {
    if (!dateString) return '날짜 없음';

    try {
      const date = new Date(dateString);
      const now = new Date();
      const diffTime = Math.abs(now - date);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

      if (diffDays === 1) {
        return '오늘';
      } else if (diffDays === 2) {
        return '어제';
      } else if (diffDays <= 7) {
        return `${diffDays - 1}일 전`;
      } else {
        return date.toLocaleDateString('ko-KR', {
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        });
      }
    } catch (error) {
      console.error('날짜 포맷팅 오류:', error);
      return '날짜 오류';
    }
  };

  // 플랫폼별 색상 및 아이콘
  const getPlatformStyle = (platform) => {
    switch (platform) {
      case 'Facebook':
        return { color: '#1877F2', name: '페이스북' };
      case 'Instagram':
        return { color: '#E4405F', name: '인스타그램' };
      case 'Threads':
        return { color: '#000000', name: '스레드' };
      default:
        return { color: '#666666', name: platform };
    }
  };

  // 초기 로드
  useEffect(() => {
    if (user?.accessToken) {
      fetchBranches();
    }
  }, [user]);

  // 지점 목록 로드 완료 후 포스팅 조회
  useEffect(() => {
    if (branches.length > 0) {
      console.log('🏢 지점 목록 로드 완료, 포스팅 조회 시작');
      fetchPosts();
    }
  }, [branches, user]);

  // 필터링 적용
  useEffect(() => {
    applyFilters();
  }, [posts, selectedBranch, selectedPlatform, searchTerm]);

  // 페이지네이션 계산
  const totalPages = Math.ceil(filteredPosts.length / postsPerPage);
  const startIndex = (page - 1) * postsPerPage;
  const endIndex = startIndex + postsPerPage;
  const currentPosts = filteredPosts.slice(startIndex, endIndex);

  // 사용자 정보가 없으면 로딩 표시
  if (!user?.accessToken) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox py={3}>
          <MDBox display="flex" justifyContent="center" alignItems="center" minHeight="400px">
            <CircularProgress />
          </MDBox>
        </MDBox>
        <Footer />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        {/* 필터 및 검색 */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Grid container spacing={3} alignItems="center">
              <Grid item xs={12} my={3}>
                <TextField
                  select
                  label="지점 선택"
                  value={selectedBranch}
                  onChange={(e) => setSelectedBranch(e.target.value)}
                  fullWidth
                  size="small"
                  sx={{ '& .MuiInputBase-root': { height: '40px' } }}
                >
                  <MenuItem value="all">전체 지점</MenuItem>
                  {branches.map((branch) => (
                    <MenuItem key={branch.id} value={branch.id}>
                      {branch.branch_name || branch.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  select
                  label="플랫폼 선택"
                  value={selectedPlatform}
                  onChange={(e) => setSelectedPlatform(e.target.value)}
                  fullWidth
                  size="small"
                  sx={{ '& .MuiInputBase-root': { height: '40px' } }}
                >
                  <MenuItem value="all">전체 플랫폼</MenuItem>
                  <MenuItem value="Facebook">페이스북</MenuItem>
                  <MenuItem value="Instagram">인스타그램</MenuItem>
                  <MenuItem value="Threads">스레드</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="검색어 입력"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="포스트 내용, 지점명, 해시태그로 검색..."
                  fullWidth
                  size="small"
                  sx={{ '& .MuiInputBase-root': { height: '40px' } }}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* 포스팅 카드 목록 */}
        <Grid container spacing={3}>
          {currentPosts.map((post) => {
            const platformStyle = getPlatformStyle(post.platform);

            return (
              <Grid item xs={12} sm={6} md={4} key={post.id}>
                <Card 
                  sx={{ 
                    height: '100%',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: 4,
                    },
                    border: `1px solid ${platformStyle.color}20`,
                  }}
                  onClick={() => handleViewDetail(post)}
                >
                  <CardContent sx={{ p: 2 }}>
                    {/* 헤더 */}
                    <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                      <MDBox display="flex" alignItems="center" gap={1}>
                        <Avatar
                          sx={{
                            width: 32,
                            height: 32,
                            bgcolor: 'primary.main',
                            fontSize: '0.75rem',
                          }}
                        >
                          {post.branchName?.charAt(0)}
                        </Avatar>
                        <MDBox>
                          <MDTypography variant="body2" fontWeight="medium" sx={{ fontSize: '0.875rem' }}>
                            {post.branchName}
                          </MDTypography>
                          <MDTypography 
                            variant="caption" 
                            sx={{ 
                              color: platformStyle.color, 
                              fontWeight: 'medium',
                              fontSize: '0.75rem'
                            }}
                          >
                            {platformStyle.name}
                          </MDTypography>
                        </MDBox>
                      </MDBox>

                      <MDTypography variant="caption" color="text">
                        {formatDate(post.postedAt)}
                      </MDTypography>
                    </MDBox>

                    {/* 이미지 미리보기 */}
                    {post.images && post.images.length > 0 && (
                      <MDBox mb={1} sx={{ position: 'relative' }}>
                        <Box
                          component="img"
                          src={post.images[0]}
                          alt="포스트 이미지"
                          sx={{
                            width: '100%',
                            height: 120,
                            objectFit: 'cover',
                            borderRadius: 1,
                            backgroundColor: '#f5f5f5',
                          }}
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                        {post.images.length > 1 && (
                          <MDTypography 
                            variant="caption" 
                            sx={{ 
                              position: 'absolute',
                              bottom: 4,
                              right: 4,
                              backgroundColor: 'rgba(0,0,0,0.6)',
                              color: 'white',
                              px: 1,
                              py: 0.5,
                              borderRadius: 1,
                              fontSize: '0.7rem'
                            }}
                          >
                            +{post.images.length - 1}개
                          </MDTypography>
                        )}
                      </MDBox>
                    )}

                    {/* 포스트 내용 미리보기 */}
                    <MDTypography
                      variant="body2"
                      color="text"
                      sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: 'vertical',
                        lineHeight: 1.4,
                        fontSize: '0.8rem',
                        mb: 1,
                      }}
                    >
                      {post.content || '내용 없음'}
                    </MDTypography>

                    {/* 해시태그 */}
                    {post.hashtags && post.hashtags.length > 0 && (
                      <MDBox display="flex" flexWrap="wrap" gap={0.5} mb={1}>
                        {post.hashtags.slice(0, 3).map((tag, index) => (
                          <Chip
                            key={index}
                            label={tag}
                            size="small"
                            sx={{ 
                              fontSize: '0.65rem',
                              height: 20,
                              backgroundColor: `${platformStyle.color}10`,
                              color: platformStyle.color,
                            }}
                          />
                        ))}
                        {post.hashtags.length > 3 && (
                          <Chip
                            label={`+${post.hashtags.length - 3}`}
                            size="small"
                            sx={{ 
                              fontSize: '0.65rem',
                              height: 20,
                              backgroundColor: '#f0f0f0',
                              color: '#666',
                            }}
                          />
                        )}
                      </MDBox>
                    )}

                    {/* 포스트 ID */}
                    <MDBox display="flex" justifyContent="flex-end">
                      <MDTypography variant="caption" color="text">
                        ID: {post.postId ? post.postId.substring(0, 8) + '...' : 'N/A'}
                      </MDTypography>
                    </MDBox>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>

        {/* 페이지네이션 */}
        {totalPages > 1 && (
          <MDBox display="flex" justifyContent="center" mt={4}>
            <Pagination
              count={totalPages}
              page={page}
              onChange={(event, value) => setPage(value)}
              color="primary"
              size="large"
            />
          </MDBox>
        )}

        {/* 상세보기 다이얼로그 */}
        <Dialog 
          open={detailOpen} 
          onClose={() => setDetailOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <MDTypography variant="h6" fontWeight="medium">
              포스팅 상세정보
            </MDTypography>
            <IconButton onClick={() => setDetailOpen(false)}>
              <Close />
            </IconButton>
          </DialogTitle>

                    {selectedPost && (
            <DialogContent sx={{ p: 0 }}>
              <MDBox p={3} display="flex" flexDirection="column" gap={3}>
                {/* 기본 정보 섹션 */}
                <Paper elevation={1} sx={{ p: 2.5, bgcolor: '#f8f9fa' }}>
                  <MDTypography variant="h6" fontWeight="medium" mb={2} color="primary">
                    📋 기본 정보
                  </MDTypography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <MDBox mb={1.5}>
                        <MDTypography variant="caption" fontWeight="medium" color="textSecondary" sx={{ textTransform: 'uppercase' }}>
                          지점명
                        </MDTypography>
                        <MDTypography variant="body1" fontWeight="medium">
                          {selectedPost.branchName}
                        </MDTypography>
                      </MDBox>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <MDBox mb={1.5}>
                        <MDTypography variant="caption" fontWeight="medium" color="textSecondary" sx={{ textTransform: 'uppercase' }}>
                          플랫폼
                        </MDTypography>
                        <MDTypography variant="body1" fontWeight="medium" sx={{ color: getPlatformStyle(selectedPost.platform).color }}>
                          {getPlatformStyle(selectedPost.platform).name}
                        </MDTypography>
                      </MDBox>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <MDBox mb={1.5}>
                        <MDTypography variant="caption" fontWeight="medium" color="textSecondary" sx={{ textTransform: 'uppercase' }}>
                          포스팅 일시
                        </MDTypography>
                        <MDTypography variant="body1">
                          {formatDate(selectedPost.postedAt)}
                        </MDTypography>
                      </MDBox>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <MDBox mb={1.5} sx={{ display: 'flex', flexDirection: 'column' }}>
                        <MDTypography variant="caption" fontWeight="medium" color="textSecondary" sx={{ textTransform: 'uppercase' }}>
                          포스트 링크
                        </MDTypography>
                     
                        {selectedPost.postUrl && (
                          <MDButton 
                            variant="outlined" 
                            color="info" 
                            size="small" 
                            sx={{ mt: 1, alignSelf: 'flex-start' }}
                            onClick={() => window.open(selectedPost.postUrl, '_blank')}
                          >
                            이동하기
                          </MDButton>
                        )}
                      </MDBox>
                    </Grid>
                  </Grid>
                </Paper>

                {/* 포스트 내용 섹션 */}
                <Paper elevation={1} sx={{ p: 2.5 }}>
                  <MDTypography variant="h6" fontWeight="medium" mb={2} color="info">
                    📝 포스트 내용
                  </MDTypography>
                  <MDBox 
                    sx={{ 
                      bgcolor: '#f8f9fa', 
                      p: 2, 
                      borderRadius: 2, 
                      border: '1px solid #e9ecef',
                      maxHeight: '200px',
                      overflowY: 'auto'
                    }}
                  >
                    <MDTypography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
                      {selectedPost.content || '내용 없음'}
                    </MDTypography>
                  </MDBox>
                </Paper>

                {/* 해시태그 섹션 */}
                {selectedPost.hashtags && selectedPost.hashtags.length > 0 && (
                  <Paper elevation={1} sx={{ p: 2.5 }}>
                    <MDTypography variant="h6" fontWeight="medium" mb={2} color="warning">
                      🏷️ 해시태그
                    </MDTypography>
                    <MDBox display="flex" flexWrap="wrap" gap={1}>
                      {selectedPost.hashtags.map((tag, index) => (
                        <Chip
                          key={index}
                          label={tag}
                          size="medium"
                          sx={{ 
                            backgroundColor: `${getPlatformStyle(selectedPost.platform).color}15`,
                            color: getPlatformStyle(selectedPost.platform).color,
                            fontWeight: 'medium',
                            '&:hover': {
                              backgroundColor: `${getPlatformStyle(selectedPost.platform).color}25`,
                            }
                          }}
                        />
                      ))}
                    </MDBox>
                  </Paper>
                )}

                {/* 이미지 섹션 */}
                {selectedPost.images && selectedPost.images.length > 0 && (
                  <Paper elevation={1} sx={{ p: 2.5 }}>
                    <MDTypography variant="h6" fontWeight="medium" mb={2} color="success">
                      🖼️ 첨부 이미지 ({selectedPost.images.length}개)
                    </MDTypography>
                    <Grid container spacing={2}>
                      {selectedPost.images.map((imageUrl, index) => (
                        <Grid item xs={6} sm={4} md={3} key={index}>
                          <Paper 
                            elevation={2} 
                            sx={{ 
                              p: 1, 
                              transition: 'all 0.2s',
                              '&:hover': {
                                elevation: 4,
                                transform: 'translateY(-2px)'
                              }
                            }}
                          >
                            <Box
                              component="img"
                              src={imageUrl}
                              alt={`포스트 이미지 ${index + 1}`}
                              sx={{
                                width: '100%',
                                height: 120,
                                objectFit: 'cover',
                                borderRadius: 1,
                                cursor: 'pointer',
                                transition: 'transform 0.2s',
                                '&:hover': {
                                  transform: 'scale(1.02)',
                                }
                              }}
                              onClick={() => window.open(imageUrl, '_blank')}
                              onError={(e) => {
                                e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjQiIGhlaWdodD0iMjQiIHZpZXdCb3g9IjAgMCAyNCAyNCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHBhdGggZD0iTTMgNkgyMVYxOEgzVjZaTTUgOFYxNkgxOVY4SDVaTTggMTBIMTZWMTJIOFYxMFoiIGZpbGw9IiNjY2MiLz4KPC9zdmc+';
                              }}
                            />
                            <MDTypography variant="caption" color="textSecondary" sx={{ mt: 0.5, display: 'block', textAlign: 'center' }}>
                              이미지 {index + 1}
                            </MDTypography>
                          </Paper>
                        </Grid>
                      ))}
                    </Grid>
                  </Paper>
                )}
              </MDBox>
            </DialogContent>
          )}

          <DialogActions>
            <MDButton onClick={() => setDetailOpen(false)} color="secondary">
              닫기
            </MDButton>
          </DialogActions>
        </Dialog>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default SocialPostsAll; 